/**
 * Payment Setup Step - Step 4 of Onboarding
 */

import { formatBillingDate, formatMoney } from '@/renderer/components/billing';
import {
  getBillingCycleDescriptor,
  getPackageMonthlyEquivalent,
  getPackageTotalPriceForCycle,
} from '@/renderer/services/billingPricing';
import { BillingCycle } from '@/renderer/types/billing';
import React from 'react';

import { useAppSelector } from '../../hooks/useRedux';
import { selectSubscriptionPackages } from '../../store/selectors';
import { Button } from '../common';

interface PaymentSetupStepProps {
  billingCycle: BillingCycle;
  checkoutStarted: boolean;
  error?: string | null;
  isLaunchingCheckout: boolean;
  isWaitingForBillingConfirmation: boolean;
  onBack: () => void;
  onOpenCheckout: () => void;
  selectedPackage?: string;
  trialEndsAt?: string | null;
}

export const PaymentSetupStep: React.FC<PaymentSetupStepProps> = ({
  billingCycle,
  checkoutStarted,
  error,
  isLaunchingCheckout,
  isWaitingForBillingConfirmation,
  onBack,
  onOpenCheckout,
  selectedPackage,
  trialEndsAt,
}) => {
  const packages = useAppSelector(selectSubscriptionPackages);
  const packageInfo = packages.find((pkg) => pkg.id === selectedPackage);

  const monthlyEquivalent = packageInfo
    ? getPackageMonthlyEquivalent(packageInfo, billingCycle)
    : null;
  const totalPrice = packageInfo
    ? getPackageTotalPriceForCycle(packageInfo, billingCycle)
    : null;

  return (
    <div className="mx-auto w-full max-w-2xl rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-pos-primary/10">
          <svg
            className="h-10 w-10 text-pos-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
        </div>
        <h2 className="mb-2 text-3xl font-bold text-pos-dark">
          Activate Your Billing
        </h2>
        <p className="mx-auto max-w-xl text-sm leading-6 text-pos-secondary">
          We&apos;ll open a secure Stripe checkout so you can confirm billing
          and start your 30-day free trial right away.
        </p>
      </div>

      <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
        <p className="text-sm font-semibold text-emerald-800">
          Your 14-day free trial starts after checkout is completed.
        </p>
        <p className="mt-1 text-sm text-emerald-700">
          {trialEndsAt
            ? `Your first charge is scheduled after ${formatBillingDate(trialEndsAt)} unless you cancel before then.`
            : 'You will not be charged until the trial period ends.'}
        </p>
      </div>

      {packageInfo && (
        <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
                Selected plan
              </p>
              <h3 className="mt-2 text-2xl font-bold text-gray-900">
                {packageInfo.name}
              </h3>
              <p className="mt-2 max-w-lg text-sm leading-6 text-gray-500">
                {packageInfo.description}
              </p>
            </div>

            <div className="rounded-2xl bg-white px-4 py-3 text-right shadow-sm">
              <p className="text-sm font-medium text-gray-400">
                {getBillingCycleDescriptor(billingCycle)}
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {monthlyEquivalent != null
                  ? formatMoney(monthlyEquivalent)
                  : 'Custom'}
                <span className="text-sm font-medium text-gray-500">
                  /month
                </span>
              </p>
              {billingCycle === 'annual' && totalPrice != null && (
                <p className="mt-1 text-sm text-gray-500">
                  {formatMoney(totalPrice)} billed once per year after trial
                </p>
              )}
            </div>
          </div>

          <ul className="mt-5 grid gap-2 sm:grid-cols-2">
            {packageInfo.features.slice(0, 4).map((feature) => (
              <li
                key={feature}
                className="flex items-start gap-2 text-sm text-gray-700"
              >
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">
          What happens next
        </h3>
        <div className="mt-4 space-y-3 text-sm text-gray-600">
          <div className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-pos-primary text-xs font-semibold text-white">
              1
            </span>
            <p>Open Stripe checkout and confirm the billing plan you chose.</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-pos-primary text-xs font-semibold text-white">
              2
            </span>
            <p>
              Complete checkout in Stripe. You can return to the app at any
              time.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-pos-primary text-xs font-semibold text-white">
              3
            </span>
            <p>
              We&apos;ll detect the billing update automatically and continue
              onboarding for you.
            </p>
          </div>
        </div>
      </div>

      {checkoutStarted && (
        <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  isWaitingForBillingConfirmation
                    ? 'animate-pulse bg-emerald-500'
                    : 'bg-emerald-500'
                }`}
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-900">
                {isWaitingForBillingConfirmation
                  ? 'Waiting for Stripe confirmation'
                  : 'Checkout started'}
              </p>
              <p className="mt-1 text-sm text-emerald-800">
                {isWaitingForBillingConfirmation
                  ? 'As soon as your billing record updates, we will move you to the next step automatically.'
                  : 'If you closed Stripe before finishing, you can open the same checkout again below.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex gap-4">
        <Button
          type="button"
          onClick={onBack}
          variant="outline"
          fullWidth
          disabled={isLaunchingCheckout}
          size="md"
        >
          Back
        </Button>
        <Button
          onClick={onOpenCheckout}
          fullWidth
          size="md"
          isLoading={isLaunchingCheckout}
        >
          {checkoutStarted
            ? 'Open Stripe Checkout Again'
            : 'Open Secure Checkout'}
        </Button>
      </div>
    </div>
  );
};
